import React, { useCallback, useEffect, useState } from 'react'
import { RouteProp } from '@react-navigation/native'
import { observer } from 'mobx-react-lite'
import RootStore, { useStore } from '../../../store'
import { SignInFlow } from '../SignIn/SignInFlow'
import { routes } from '@linqpal/models'
import cookies from '../../../utils/service/cooker'
import fb from '../../../utils/service/fb'
import { ScreenWrapper } from '../InvoiceDetails/components/ScreenWrapper'
import Loading from '../../Loading'
import { PAGE_MODE } from '../InvoiceDetails/utils/pageMode'
import { useCheckAndGetInvoicePayload } from './hooks/checkAndGetInvoiceFromJwt'
import { JwtInvoiceDetails } from './JwtInvoiceDetails'
import { JwtInvoiceDetailsIdle } from './JwtInvoiceDetailsIdle'
import { IntegrationRedirectStatusesType } from '@linqpal/models/src/dictionaries'
import { IntegrationPaymentErrorModal } from './components/IntegrationErrorModal'
import { SignUpFlow } from '../SignUp/SignUpFlow'
import { SIGN_IN_STEPS, State } from '../SignIn/types'

enum STEPS {
  IDLE,
  SIGN_IN,
  SIGN_UP,
  INVOICE_DETAILS,
}

interface IntegrationJwtPaymentProps {
  route: RouteProp<
    { params: { jwt?: string; mode?: PAGE_MODE; redirectUrl?: string } },
    'params'
  >
}

export const IntegrationJwtPayment: React.FC<IntegrationJwtPaymentProps> =
  observer(({ route }) => {
    const { userStore } = RootStore
    const { remoteConfig } = useStore()
    const { isAuthenticated, setIsAuthenticated, setAuthState, user } =
      userStore

    const [width, setWidth] = useState<number>(window.innerWidth)
    const [signInInitialStep, setSignInInitialStep] = useState(
      SIGN_IN_STEPS.SIGN_IN_LOGIN,
    )
    const [signInInitialState, setSignInInitialState] = useState<State>({})

    function handleWindowSizeChange() {
      setWidth(window.innerWidth)
    }
    useEffect(() => {
      window.addEventListener('resize', handleWindowSizeChange)
      return () => {
        window.removeEventListener('resize', handleWindowSizeChange)
      }
    }, [])

    const isWeb = width > 575

    const { jwt, mode, redirectUrl } = route?.params ?? {}

    const [step, setStep] = useState(STEPS.IDLE)

    const minBnplAmount =
      (remoteConfig.get('de_min_approval_amount') as number) || 5

    const {
      invoice,
      loading,
      errorCode,
      isInvoicePaid,
      invoiceCreditStatus,
      invoicePaymentStatus,
    } = useCheckAndGetInvoicePayload(jwt)

    const handleNext = useCallback(async () => {
      if ([STEPS.SIGN_IN, STEPS.SIGN_UP].includes(step) && isAuthenticated) {
        setStep(STEPS.INVOICE_DETAILS)
      }
    }, [step, isAuthenticated])

    const handleAuthChange = useCallback(() => {
      if (step === STEPS.SIGN_UP) {
        setStep(STEPS.SIGN_IN)
      }
      if (step === STEPS.SIGN_IN) {
        setStep(STEPS.SIGN_UP)
      }
    }, [step])

    const handleSignUpUserExists = useCallback((authData: State) => {
      setSignInInitialState(authData)
      setSignInInitialStep(SIGN_IN_STEPS.SIGN_IN_PASSWORD)
      setStep(STEPS.SIGN_IN)
    }, [])

    const handleLogin = useCallback(async () => {
      if (step === STEPS.IDLE && !isAuthenticated) {
        setStep(STEPS.SIGN_IN)
      }
    }, [step, isAuthenticated])

    const handleRedirectBack = useCallback(
      async (status: IntegrationRedirectStatusesType) => {
        if (mode === PAGE_MODE.POPUP) {
          console.log('paymentSuccess')
          window?.opener?.postMessage({ status }, '*')
          window?.parent?.postMessage({ status }, '*')
        } else if (mode === PAGE_MODE.REDIRECT) {
          if (redirectUrl) {
            const url = new URL(redirectUrl)
            url.searchParams.append('status', status)

            window.location.href = url.toString()
          }
        }
      },
      [mode, redirectUrl],
    )

    const handleLogOut = useCallback(async () => {
      console.log('Log out, clearing session')
      await routes.user.logout()
      setAuthState(null)
      setIsAuthenticated(false)
      cookies.remove('session')
      await fb.auth().signOut()
      setStep(STEPS.IDLE)
    }, [setAuthState, setIsAuthenticated])

    useEffect(() => {
      if (invoice && isAuthenticated && !isInvoicePaid) {
        setStep(STEPS.INVOICE_DETAILS)
      }
    }, [invoice, isAuthenticated, isInvoicePaid])

    if (loading) {
      return <Loading />
    }

    if (errorCode) {
      return (
        <IntegrationPaymentErrorModal
          errorCode={errorCode}
          onClose={handleRedirectBack}
        />
      )
    }

    return (
      <>
        {step === STEPS.SIGN_IN && (
          <SignInFlow
            onSuccess={handleNext}
            onSignUp={handleAuthChange}
            initialStep={signInInitialStep}
            initialState={signInInitialState}
            mode={mode}
          />
        )}
        {step === STEPS.SIGN_UP && (
          <SignUpFlow
            onSuccess={handleNext}
            onLogin={handleAuthChange}
            onUserExists={handleSignUpUserExists}
          />
        )}
        {step === STEPS.INVOICE_DETAILS && (
          <JwtInvoiceDetails
            invoice={invoice}
            minBnplAmount={minBnplAmount}
            onLogOut={handleLogOut}
            onRedirectBack={handleRedirectBack}
            invoiceCreditStatus={invoiceCreditStatus}
            invoicePaymentStatus={invoicePaymentStatus}
            isInvoicePaid={!!isInvoicePaid}
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            jwtToken={jwt!}
            mode={mode}
            isWeb={isWeb}
          />
        )}
        {step === STEPS.IDLE &&
          (isWeb ? (
            <ScreenWrapper
              email={user?.email ? user.email : user?.login ? user.login : ''}
              isWeb={isWeb}
            >
              <JwtInvoiceDetailsIdle
                invoice={invoice}
                invoicePaymentStatus={invoicePaymentStatus}
                isInvoicePaid={!!isInvoicePaid}
                invoiceCreditStatus={invoiceCreditStatus}
                onRedirectBack={handleRedirectBack}
                onPress={handleLogin}
                isWeb={isWeb}
              />
            </ScreenWrapper>
          ) : (
            <JwtInvoiceDetailsIdle
              invoice={invoice}
              invoicePaymentStatus={invoicePaymentStatus}
              isInvoicePaid={!!isInvoicePaid}
              invoiceCreditStatus={invoiceCreditStatus}
              onRedirectBack={handleRedirectBack}
              onPress={handleLogin}
              isWeb={isWeb}
            />
          ))}
      </>
    )
  })
